import messageResource from "../messageResource"
import {escapeHtmlSymbols} from "../atUtils"
import {disableTextSelection, atImgPath, addClass} from "../atBrowserUtils"

/**
 * Abstract class
 * Renders rows with user groups
 */
export default class GroupRenderer {
    /**
     * @param config.groupDivClassName class name of user group row
     * @param config.groupTableClassName class name of first table in user group div
     * @param config.folderImgClassName class name of cell with folder image
     * @param config.deleteGroupLinkClassName class name of user group delete button
     * @param config.groupNameCellClassName class name of cell with user group name
     * @param config.counterSpanClassName class name of span with counter of users in user group
     * @param config.waitingAnimationCellClassName class name of cell with waiting ajax animation
     * @param config.editTableCellClassName class name of cell for edited group table
     * @param config.editInputClassName class name of edit user group input field
     * @param config.undoButtonClassName class name of user group undo edit button
     * @param config.acceptButtonClassName class name of user group accept edit button
     * @param config.nullGroupClassName class name of row of bogus 'without user group' user group
     * @param config.defaultGroupClassName class name of row of default group
     * @param config.confirmDeleteCellClassName class name of cell for confirm delete group
     * @param config.confirmDeleteAcceptButtonClassName class name of accept confirm delete button
     * @param config.confirmDeleteUndoButtonClassName class name of undo confirm delete button
     * @param config.groupDivIdPrefix dom id prefix for user group row
     * @param config.deleteLinkIdPrefix dom id prefix for user group delete button
     * @param config.undoEditIdPrefix dom id prefix for user group undo edit button
     * @param config.acceptEditIdPrefix dom id prefix for user group accept edit button
     * @param config.editInputIdPrefix dom id prefix for user group edit input field
     * @param config.nameCellIdPrefix dom id prefix for cell with user group name
     * @param config.inputCellIdPrefix dom id prefix for cell with edit user group input field
     * @param config.editGroupCellIdPrefix dom id prefix for cell for edited group table
     * @param config.waitingAnimationCellIdPrefix  dom id prefix for cell with waiting ajax animation
     * @param config.spinnerContainerIdPrefix  dom id prefix for spinner container div
     * @param config.confirmDeleteCellIdPrefix dom id prefix for group delete cell
     * @param config.confirmDeleteAcceptButtonIdPrefix dom id prefix for group accept delete button
     * @param config.confirmDeleteUndoButtonIdPrefix dom id prefix for group undo delete button
     */
    init(config) {
        this.groupDivClassName = config.groupDivClassName;
        this.groupTableClassName = config.groupTableClassName;
        this.folderImgClassName = config.folderImgClassName;
        this.deleteGroupLinkClassName = config.deleteGroupLinkClassName;
        this.groupNameCellClassName = config.groupNameCellClassName;
        this.counterSpanClassName = config.counterSpanClassName;
        this.waitingAnimationCellClassName = config.waitingAnimationCellClassName;
        this.editTableCellClassName = config.editTableCellClassName;
        this.editInputClassName = config.editInputClassName;
        this.undoButtonClassName = config.undoButtonClassName;
        this.acceptButtonClassName = config.acceptButtonClassName;
        this.nullGroupClassName = config.nullGroupClassName;
        this.defaultGroupClassName = config.defaultGroupClassName;
        this.confirmDeleteCellClassName = config.confirmDeleteCellClassName;
        this.confirmDeleteAcceptButtonClassName = config.confirmDeleteAcceptButtonClassName;
        this.confirmDeleteUndoButtonClassName = config.confirmDeleteUndoButtonClassName;
        this.groupDivIdPrefix = config.groupDivIdPrefix;
        this.deleteLinkIdPrefix = config.deleteLinkIdPrefix;
        this.undoEditIdPrefix = config.undoEditIdPrefix;
        this.acceptEditIdPrefix = config.acceptEditIdPrefix;
        this.editInputIdPrefix = config.editInputIdPrefix;
        this.nameCellIdPrefix = config.nameCellIdPrefix;
        this.inputCellIdPrefix = config.inputCellIdPrefix;
        this.editGroupCellIdPrefix = config.editGroupCellIdPrefix;
        this.waitingAnimationCellIdPrefix = config.waitingAnimationCellIdPrefix;
        this.spinnerContainerIdPrefix = config.spinnerContainerIdPrefix;
        this.confirmDeleteCellIdPrefix = config.confirmDeleteCellIdPrefix;
        this.confirmDeleteAcceptButtonIdPrefix = config.confirmDeleteAcceptButtonIdPrefix;
        this.confirmDeleteUndoButtonIdPrefix = config.confirmDeleteUndoButtonIdPrefix;
        this.groupNamingKey = config.groupNamingKey;
        this.GroupNamingKey = config.GroupNamingKey;
        this.validatorGroupStrategy = config.validatorGroupStrategy;
        return this;
    }

    buildNameCell(group) {
        const nameTd = document.createElement("td");
        nameTd.id = formatId(this.nameCellIdPrefix, group.id);
        nameTd.className = this.groupNameCellClassName;

        const containerDiv = document.createElement("div");
        const overflowDiv = document.createElement("div");
        overflowDiv.className = GroupManagementUtil.ellipsisOverflowClassName;
        const name = this.getNameForNameCell(group);
        overflowDiv.title = name;
        overflowDiv.innerHTML = escapeHtmlSymbols(name);
        nameTd.appendChild(containerDiv);
        containerDiv.appendChild(overflowDiv);
        disableTextSelection(overflowDiv);

        containerDiv.appendChild(this.buildNameEditCell(group));

        return nameTd;
    }

    getNameForNameCell(group) {
        return group.name
    }

    buildCountCell(group) {
        const count = group.countOfUserWithEnabledAccess();
        const countTd = document.createElement("td");
        countTd.className = this.counterSpanClassName;
        if (count === 0) countTd.className += " zeroCount";
        countTd.innerHTML = '(' + count + ')';
        disableTextSelection(countTd);

        return countTd;
    }

    buildDeleteCell(group) {
        const deleteTd = document.createElement("td");
        deleteTd.className = this.deleteGroupLinkClassName;
        const deleteLink = this.makeBlankLink();

        const deleteSpan = document.createElement("span");
        deleteSpan.id = formatId(this.deleteLinkIdPrefix, group.id);

        deleteTd.appendChild(deleteLink);
        deleteLink.appendChild(deleteSpan);

        return deleteTd;
    }

    buildWaitingAnimationCell(group) {
        const waitingAnimationTd = document.createElement("td");
        waitingAnimationTd.className = this.waitingAnimationCellClassName;
        waitingAnimationTd.id = formatId(this.waitingAnimationCellIdPrefix, group.id);

        const spinnerContainer = document.createElement("div");
        spinnerContainer.id = formatId(this.spinnerContainerIdPrefix, group.id);
        waitingAnimationTd.appendChild(spinnerContainer);

        return waitingAnimationTd;
    }

    buildConfirmDeleteCell(group) {
        const confirmDeleteTd = document.createElement("td");
        confirmDeleteTd.className = this.confirmDeleteCellClassName;
        confirmDeleteTd.id = formatId(this.confirmDeleteCellIdPrefix, group.id);

        const table = document.createElement("table");
        const tbody = document.createElement("tbody");
        const tr = document.createElement("tr");

        const confirmDeleteTextTd = document.createElement("td");
        const confirmDeleteText = document.createElement('span');
        confirmDeleteText.innerHTML = this.generateDeleteConfirmMessage();
        confirmDeleteTextTd.appendChild(confirmDeleteText);

        const confirmDeleteButtonTd = document.createElement("td");
        const confirmDeleteButton = document.createElement('button');
        confirmDeleteButton.className = this.confirmDeleteAcceptButtonClassName;
        confirmDeleteButton.id = formatId(this.confirmDeleteAcceptButtonIdPrefix, group.id);
        confirmDeleteButton.innerHTML = messageResource.getMessage('group_management.confirm_delete.confirm');
        confirmDeleteButtonTd.appendChild(confirmDeleteButton);

        const cancelDeleteButtonTd = document.createElement("td");
        const cancelDeleteButton = document.createElement('div');
        cancelDeleteButton.className = this.confirmDeleteUndoButtonClassName;
        cancelDeleteButton.id = formatId(this.confirmDeleteUndoButtonIdPrefix, group.id);
        cancelDeleteButton.innerHTML = messageResource.getMessage('group_management.confirm_delete.cancel');
        cancelDeleteButtonTd.appendChild(cancelDeleteButton);

        table.appendChild(tbody);
        tbody.appendChild(tr);
        tr.appendChild(confirmDeleteTextTd);
        tr.appendChild(confirmDeleteButtonTd);
        tr.appendChild(cancelDeleteButtonTd);
        confirmDeleteTd.appendChild(table);

        return confirmDeleteTd;
    }

    /**
     * Build table for edited group
     * @param group GroupWithUsers instance
     */
    buildNameEditCell(group) {
        const editCell = document.createElement("div");
        editCell.className = this.editTableCellClassName;
        editCell.id = formatId(this.editGroupCellIdPrefix, group.id);
        const table = document.createElement("table");
        table.id = formatId(this.inputCellIdPrefix, group.id);
        const tbody = document.createElement("tbody");
        const tr = document.createElement("tr");

        const inputTd = document.createElement("td");
        inputTd.className = this.editInputClassName;
        const editInput = document.createElement("input");
        editInput.type = "text";
        editInput.value = group.name;
        editInput.id = formatId(this.editInputIdPrefix, group.id);
        editInput.maxLength = "40";
        inputTd.appendChild(editInput);

        const undoTd = document.createElement("td");
        undoTd.className = this.undoButtonClassName;
        undoTd.id = formatId(this.undoEditIdPrefix, group.id);

        const undoDiv = document.createElement("div");
        const undoImg = document.createElement("img");
        undoImg.src = atImgPath("user_group_lightbox/cancel.png");
        undoDiv.appendChild(undoImg);
        undoTd.appendChild(undoDiv);

        const acceptTd = document.createElement("td");
        acceptTd.className = this.acceptButtonClassName;
        acceptTd.id = formatId(this.acceptEditIdPrefix, group.id);

        const acceptDiv = document.createElement("div");
        const acceptImg = document.createElement("img");
        acceptImg.src = atImgPath("user_group_lightbox/green-tick.png");
        acceptDiv.appendChild(acceptImg);
        acceptTd.appendChild(acceptDiv);

        editCell.appendChild(table);
        table.appendChild(tbody);
        tbody.appendChild(tr);
        tr.appendChild(inputTd);
        tr.appendChild(acceptTd);
        tr.appendChild(undoTd);

        return editCell;
    }

    /**
     * Build columns for group row
     */
    fillTable(tableTr, group) {
        // abstract
    }

    /**
     * Generate one row for group
     * @param group GroupWithUsers instance
     */
    generateContent(group) {
        const div = document.createElement("div");
        div.className = this.groupDivClassName;
        if (this.validatorGroupStrategy.isNullGroup(group)) addClass(div, this.nullGroupClassName);
        if (this.validatorGroupStrategy.isDefaultGroup(group)) addClass(div, this.defaultGroupClassName);
        div.id = formatId(this.groupDivIdPrefix, group.id);

        const table = document.createElement("table");
        table.className = this.groupTableClassName;

        this.fillTable(table, group);

        div.appendChild(table);

        return div;
    }

    /**
     * Make blank link with killed href
     */
    makeBlankLink() {
        const link = document.createElement("a");
        link.href = "javascript: void(0)";
        return link;
    }

    generateDeleteConfirmMessage() {
        // abstract
    }

    getItemText(group) {
        return escapeHtmlSymbols(group.name);
    }
}


// WEBPACK FOOTER //
// ./web/js/groupManagement/group_renderer.js